/**
 * One analysis's own page.
 *
 * Every chosen analysis resolves here, whichever engine computed it. Blocked,
 * failed, not-run and a genuine result are four different states and look
 * different — never a tabbed, empty table standing in for "this has not run".
 * The result (and exceptions, and supporting rows, when it has them) is paged
 * from the server — search runs over the whole stored result, not one page.
 * "Download Excel" is the server's complete workbook; "Download filtered
 * rows" writes just the rows the search matched. See
 * docs/ui-design.md §4.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { Segmented } from '../shared/Segmented';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '../../provider/toast';
import {
	downloadAnalysisExcel,
	fetchMatchingRows,
	useAnalysisDetailQuery,
	useAnalysisOverviewQuery,
	useAnalysisResultQuery,
	useRunAnalysesMutation,
} from '../../queries/analysis';
import { useAuth } from '../../provider/auth';
import { PageHeader } from '../../provider/pageTitle';
import { Note } from '../../shared/ui';
import { Figure, Figures } from '../shared/badges';
import { count, dateTime, decimal, durationLabel } from '../../utils/format';
import { downloadWorkbook } from '../../utils/excel';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
	type FilterSpec,
} from '../shared/tables';
import { EmptyState, ErrorState, LoadingState } from '../shared/states';
import { SourceLineageDrawer } from '../shared/SourceLineageDrawer';
import { useAuditContext } from '../audits/AuditWorkspace';
import { AnalysisStatusTag } from './AnalysisStatusTag';
import { startedAgoLabel } from './analysisStatusPresentation';
import type { AnalysisOverviewRow, ResultPage } from '../../schemas';
import { ApiError } from '../../api/errors';
import { auditPath } from '../modules/flow';

const LOCKED_REASON =
	'The report has been issued, so this analysis cannot be re-run.';

type Part = 'result' | 'exceptions' | 'supporting';

const PART_LABEL: Record<Part, string> = {
	result: 'Result',
	exceptions: 'Exceptions',
	supporting: 'Supporting rows',
};

/** A number at the precision it carries: whole numbers grouped, others to 2dp. */
function figure(value: number): string {
	return Number.isInteger(value) ? count(value) : decimal(value, 2);
}

/** `/audits/:id/analyses/:slug` — one analysis on its own page. */
export default function AnalysisDetail() {
	const { analysisSlug = '' } = useParams();
	return <AnalysisDetailView slug={analysisSlug} />;
}

/**
 * One analysis's view. On its own page it fills the page header; `embedded`
 * (a report inside an analysis's page, which owns the header) puts the same
 * status line and actions at the top of the view instead.
 */
export function AnalysisDetailView({
	slug: analysisSlug,
	embedded = false,
}: {
	slug: string;
	embedded?: boolean;
}) {
	const { audit, crumbs: auditCrumbs } = useAuditContext();
	const { permissions } = useAuth();
	const canRun = Boolean(permissions?.runAnalyses);
	const toast = useToast();

	const detail = useAnalysisDetailQuery(audit.pkAuditId, analysisSlug);
	/* Read for `locked` only — the overview already carries the audit-wide
     "the report is issued" state, computed once on the server rather than
     re-derived here from the audit's own status. */
	const overview = useAnalysisOverviewQuery(audit.pkAuditId);
	const locked = Boolean(overview.data?.locked);
	const rerun = useRunAnalysesMutation(audit.pkAuditId);

	/* "Started 42s ago" has to move between polls too: a poll that returns the
     same row does not re-render this page, so tick once a second while the
     analysis is running. */
	const running = detail.data?.analysis.status === 'RUNNING';
	const [, setTick] = useState(0);
	useEffect(() => {
		if (!running) return;
		const timer = setInterval(() => setTick((tick) => tick + 1), 1000);
		return () => clearInterval(timer);
	}, [running]);

	const crumbs = [
		...auditCrumbs,
		{ label: 'Analyses', to: `${auditPath(audit)}/analyses` },
	];

	if (detail.isPending) return <LoadingState label="Loading the result…" />;
	if (detail.error)
		return <ErrorState error={detail.error} onRetry={detail.refetch} />;
	if (!detail.data) return null;

	const { analysis: row } = detail.data;
	const busy = row.status === 'RUNNING' || row.status === 'QUEUED';
	const blocked =
		row.availability === 'BLOCKED' &&
		row.status !== 'RUNNING' &&
		row.status !== 'QUEUED';
	const start = () =>
		rerun.mutate([row.slug], {
			onSuccess: () => toast('ok', 'Analysis started.'),
			onError: (error) =>
				toast(
					'danger',
					error instanceof Error ? error.message : 'Could not start.',
				),
		});

	const statusLine = (
		<Stack
			component="span"
			direction="row"
			spacing={1}
			alignItems="center"
			flexWrap="wrap"
		>
			{!embedded && (
				<Typography component="span" variant="inherit">
					Ref. {row.ref}
				</Typography>
			)}
			<AnalysisStatusTag row={row} help={row.blockReason ?? row.error} />
			{row.completedAt && (
				<Typography component="span" variant="inherit">
					Last run {dateTime(row.completedAt)}
				</Typography>
			)}
			{row.durationMs !== null && (
				<Typography component="span" variant="inherit">
					Duration {durationLabel(row.durationMs)}
				</Typography>
			)}
		</Stack>
	);
	const actions = (
		<Box display="flex" gap="var(--s2)">
			{canRun &&
				!blocked &&
				(row.status === 'COMPLETED' || row.status === 'FAILED') && (
					<Button
						variant="outlined"
						disabled={locked || rerun.isPending}
						title={locked ? LOCKED_REASON : undefined}
						onClick={start}
					>
						{row.status === 'FAILED' ? 'Retry' : 'Re-run'}
					</Button>
				)}
			{/* Inside an analysis the download is the analysis's own workbook,
          offered once in the analysis's header. */}
			{!embedded && <DownloadExcel auditId={audit.pkAuditId} row={row} />}
		</Box>
	);

	return (
		<Stack gap="var(--s5)" sx={{ minWidth: 0 }}>
			{/* Inside an analysis's page the header already carries the status,
          the run time, Re-run and Download Excel. */}
			{embedded ? null : (
				<PageHeader
					breadcrumbs={crumbs}
					title={row.name}
					subtitle={statusLine}
					actions={actions}
				/>
			)}

			{blocked && (
				<EmptyState title="Blocked">
					{row.missingColumns.length > 0 ? (
						<>
							<Typography component="p" variant="inherit">
								Missing required fields:
							</Typography>
							<Box component="ul">
								{row.missingColumns.map((column) => (
									<Box component="li" key={column.key}>
										{column.label}
									</Box>
								))}
							</Box>
						</>
					) : (
						<Typography component="p" variant="inherit">
							{row.blockReason ??
								'The selected data cannot feed this analysis.'}
						</Typography>
					)}
				</EmptyState>
			)}

			{!blocked && row.status === 'FAILED' && (
				<EmptyState
					title="Failed"
					action={
						canRun && !locked ? (
							<Button
								variant="contained"
								disabled={rerun.isPending}
								onClick={start}
							>
								Retry
							</Button>
						) : undefined
					}
				>
					Reason: {row.error || 'No reason was recorded.'}
				</EmptyState>
			)}

			{!blocked && row.status === 'NOT_RUN' && (
				<EmptyState
					title="Not run"
					action={
						canRun && !locked ? (
							<Button
								variant="contained"
								disabled={rerun.isPending}
								onClick={start}
							>
								Run analysis
							</Button>
						) : undefined
					}
				>
					{row.description}
				</EmptyState>
			)}

			{!blocked && busy && (
				<Note
					tone="info"
					row
					title={row.status === 'QUEUED' ? 'Waiting' : 'Running'}
				>
					{row.status === 'QUEUED'
						? 'Waiting to start.'
						: (startedAgoLabel(row.startedAt) ?? 'Started.')}{' '}
					This page updates by itself when the result is ready.
				</Note>
			)}

			{!blocked && row.status === 'COMPLETED' && (
				<>
					{row.outdated && (
						<Note tone="warn" row title="Out of date">
							The data scope changed after this ran. Re-run to
							update.{' '}
							{canRun && !locked && (
								<Button
									size="small"
									variant="outlined"
									disabled={rerun.isPending}
									onClick={start}
								>
									Re-run
								</Button>
							)}
						</Note>
					)}

					<Figures>
						<Figure
							label="Rows analysed"
							value={
								row.rowsAnalysed !== null
									? count(row.rowsAnalysed)
									: '—'
							}
							// meta="Rows in this audit's data scope"
							icon={<StorageOutlinedIcon />}
						/>
						<Figure
							label="Result rows"
							value={
								row.resultRows !== null
									? count(row.resultRows)
									: '—'
							}
							// meta="Rows this analysis returned"
							icon={<TableRowsOutlinedIcon />}
							tone="accent"
						/>
						<Figure
							label="Exception findings"
							value={
								row.exceptionFindings === null
									? 'Not calculated'
									: count(row.exceptionFindings)
							}
							small={row.exceptionFindings === null}
							// meta={
							//   row.exceptionFindings === null
							//     ? undefined
							//     : row.exceptionFindings > 0
							//       ? 'Rows flagged for review'
							//       : 'Nothing flagged'
							// }
							icon={<ReportProblemOutlinedIcon />}
							tone={
								row.exceptionFindings !== null &&
								row.exceptionFindings > 0
									? 'bad'
									: 'ok'
							}
						/>
					</Figures>

					{row.hasResult && <ResultView row={row} />}
				</>
			)}
		</Stack>
	);
}

function DownloadExcel({
	auditId,
	row,
}: {
	auditId: string;
	row: AnalysisOverviewRow;
}) {
	const toast = useToast();
	const download = useMutation({
		mutationFn: () => downloadAnalysisExcel(auditId, row.slug),
		onError: (error) =>
			toast(
				'danger',
				error instanceof Error ? error.message : 'The download failed.',
			),
	});
	const disabledReason = !row.hasExcel
		? (row.excelUnavailableReason ??
			'There is no Excel file for this result.')
		: null;
	return (
		<Button
			variant="contained"
			loading={download.isPending}
			disabled={Boolean(disabledReason) || download.isPending}
			title={disabledReason ?? undefined}
			onClick={() => download.mutate()}
		>
			Download Excel
		</Button>
	);
}

/** Why a result has no exception rows to show (`exceptionFindings` null). */
function exceptionsUnavailableReason(row: AnalysisOverviewRow): string {
	if (row.exceptionCalculation === 'DISABLED')
		return 'Exception calculation is disabled for this analysis.';
	if (row.exceptionCalculation === 'NOT_IMPLEMENTED')
		return 'Exception rules have not been set up for this analysis yet.';
	/* Enabled now, but this result was produced without them (an older
     result, or one run while calculation was switched off). */
	return 'Exception rows are not available for this result. Run the analysis again.';
}

/** The server saying "this part is not in this result" (409), as opposed to
 *  a fault worth retrying. */
function isRefusal(error: unknown): error is ApiError {
	return error instanceof ApiError && error.status === 409;
}

function ResultView({ row }: { row: AnalysisOverviewRow }) {
	const { audit } = useAuditContext();
	const toast = useToast();
	/* Full screen sits in the filter row, outside the table. */
	const fullScreen = useFullScreen(row.name);
	/* Exceptions are the flagged subset of Result in Result's order, so equal
     counts mean the same rows — an exception-only report would show its
     result twice. The tab appears only when the two differ. */
	const exceptionsRepeatResult =
		row.exceptionFindings !== null &&
		row.exceptionFindings === row.resultRows;
	const tabs: Part[] = [
		'result',
		...(exceptionsRepeatResult ? [] : (['exceptions'] as const)),
		...(row.supportingRows && row.supportingRows > 0
			? (['supporting'] as const)
			: []),
	];
	const [part, setPart] = useState<Part>('result');
	/* The row count beside each view's name, as the filter chips show theirs. */
	const partCount = (which: Part) => {
		const value =
			which === 'result'
				? row.resultRows
				: which === 'exceptions'
					? row.exceptionFindings
					: row.supportingRows;
		return value === null ? '' : count(value);
	};
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [filters, setFilters] = useState<{ q: string; column: string }>({
		q: '',
		column: '',
	});
	/* Searched on the server, so wait for the typing to settle. */
	const [query, setQuery] = useState(filters);
	useEffect(() => {
		const timer = setTimeout(() => setQuery(filters), 300);
		return () => clearTimeout(timer);
	}, [filters]);
	useEffect(() => setPage(1), [query, part, row.completedAt]);

	/* Decided by what this RESULT holds, not today's setting: a count means
     the exception rows were calculated (page them, whatever the setting is
     now); no count means they were not, and the message says why — a static
     message, never a network call. */
	const exceptionsUnavailable =
		part === 'exceptions' && row.exceptionFindings === null;

	const result = useAnalysisResultQuery(
		audit.pkAuditId,
		row.slug,
		{
			part,
			page,
			pageSize,
			q: query.q.trim() || undefined,
			column: query.column || undefined,
		},
		!exceptionsUnavailable,
	);
	const [lineage, setLineage] = useState<{
		position: number;
		values: ResultPage['rows'][number]['values'];
	} | null>(null);

	const searching = Boolean(query.q.trim());
	const download = useMutation({
		mutationFn: async () => {
			const matched = await fetchMatchingRows(audit.pkAuditId, row.slug, {
				part,
				q: query.q.trim(),
				column: query.column || undefined,
			});
			await downloadWorkbook({
				fileName: `${audit.clientCode} - ${audit.name} - ${row.ref} ${row.name} (filtered)`,
				sheetName: PART_LABEL[part],
				columns: matched.columns,
				rows: matched.rows.map((item) => item.values),
				about: [
					['Analysis', `${row.ref} ${row.name}`],
					['Client', `${audit.clientName} (${audit.clientCode})`],
					['Audit', audit.name],
					[
						'Filter',
						`${query.column ? `${matched.columns.find((c) => c.key === query.column)?.label ?? ''} ` : ''}contains "${query.q.trim()}"`,
					],
					['Rows', String(matched.rows.length)],
					[
						'Last run',
						row.completedAt ? dateTime(row.completedAt) : '—',
					],
				],
			});
		},
		onError: (error) =>
			toast(
				'danger',
				error instanceof Error ? error.message : 'The download failed.',
			),
	});

	const data = exceptionsUnavailable ? undefined : result.data;

	const columns: Array<Column<ResultPage['rows'][number]>> = data
		? [
				{
					key: '__n',
					label: '#',
					width: 64,
					num: true,
					render: (item) => (
						<Typography
							component="span"
							variant="inherit"
							className="mut"
						>
							{count(item.position + 1)}
						</Typography>
					),
				},
				...data.columns.map(
					(column, index): Column<ResultPage['rows'][number]> => ({
						key: column.key,
						label: column.label,
						num: column.numeric,
						width: column.numeric ? 140 : 180,
						render: (item) => {
							const value = item.values[index];
							if (
								value === null ||
								value === undefined ||
								value === ''
							)
								return (
									<Typography
										component="span"
										variant="inherit"
										className="mut"
									>
										—
									</Typography>
								);
							return typeof value === 'number'
								? figure(value)
								: String(value);
						},
					}),
				),
				...(data.lineageRunId
					? [
							{
								key: '__source',
								label: 'Source',
								width: 100,
								render: (item: ResultPage['rows'][number]) => (
									<Button
										size="small"
										variant="text"
										onClick={() =>
											setLineage({
												position: item.position,
												values: item.values,
											})
										}
									>
										Source
									</Button>
								),
							},
						]
					: []),
			]
		: [];

	const totals: Record<string, string> | undefined =
		data && Object.keys(data.sums).length > 0
			? Object.fromEntries(
					Object.entries(data.sums).map(([key, value]) => [
						key,
						figure(value),
					]),
				)
			: undefined;

	const specs: FilterSpec[] = [
		{
			key: 'column',
			label: 'Column',
			kind: 'select',
			options: [
				{ value: '', label: 'All columns' },
				...(data?.columns ?? []).map((column) => ({
					value: column.key,
					label: column.label,
				})),
			],
		},
		{
			key: 'q',
			label: 'Contains',
			kind: 'search',
			placeholder: 'Search this result',
		},
	];

	return (
		<Stack gap="var(--s3)" sx={{ minWidth: 0 }}>
			{tabs.length > 1 && (
				<Segmented
					value={part}
					onChange={setPart}
					ariaLabel="Result sections"
					options={tabs.map((tabPart) => ({
						value: tabPart,
						label: `${PART_LABEL[tabPart]} ${partCount(tabPart)}`,
					}))}
				/>
			)}

			{exceptionsUnavailable ? (
				<EmptyState title={PART_LABEL.exceptions}>
					{exceptionsUnavailableReason(row)}
				</EmptyState>
			) : (
				<>
					{data &&
						data.storedRows < data.resultRows &&
						part !== 'exceptions' && (
							<Note tone="info" row>
								Showing the first {count(data.storedRows)} of{' '}
								{count(data.resultRows)} rows. Download Excel
								contains every row.
							</Note>
						)}

					<FilterBar
						specs={specs}
						values={filters}
						onChange={(key, value) =>
							setFilters((current) => ({
								...current,
								[key]: value,
							}))
						}
						onClear={() => setFilters({ q: '', column: '' })}
						resultNote={
							data && searching
								? `${count(data.total)} matching rows`
								: undefined
						}
						actions={
							<>
								{searching && data && data.total > 0 && (
									<Button
										size="small"
										variant="outlined"
										loading={download.isPending}
										disabled={download.isPending}
										onClick={() => download.mutate()}
									>
										{`Download ${count(data.total)} filtered rows`}
									</Button>
								)}
								{fullScreen.button}
							</>
						}
					/>

					{result.error &&
					part === 'exceptions' &&
					isRefusal(result.error) ? (
						/* The result has a count but no stored rows (an older result):
               a fact about this result, not a fault to retry. */
						<EmptyState title={PART_LABEL.exceptions}>
							{result.error.message}
						</EmptyState>
					) : result.error ? (
						<ErrorState
							error={result.error}
							onRetry={result.refetch}
						/>
					) : (
						<Box className="card">
							<Box className="card-bd flush">
								<DataTable
									fullScreen={fullScreen}
									rows={data?.rows ?? []}
									columns={
										columns.length
											? columns
											: [
													{
														key: 'loading',
														label: '',
														render: () => null,
													},
												]
									}
									rowKey={(item) => String(item.position)}
									caption={`${row.name} — ${PART_LABEL[part].toLowerCase()}`}
									loading={
										result.isPending ||
										(result.isFetching && !data)
									}
									totals={
										totals && part === 'result'
											? totals
											: undefined
									}
									empty={
										searching
											? 'No rows match this search.'
											: part === 'exceptions'
												? 'No findings.'
												: 'This result has no rows.'
									}
									paging={
										data
											? {
													page,
													pageSize,
													total: data.total,
													onPageChange: setPage,
													onPageSizeChange: (
														size,
													) => {
														setPageSize(size);
														setPage(1);
													},
												}
											: undefined
									}
								/>
							</Box>
						</Box>
					)}
				</>
			)}

			<SourceLineageDrawer
				runId={data?.lineageRunId ?? null}
				row={lineage?.position ?? null}
				onClose={() => setLineage(null)}
				context={
					lineage && data
						? {
								analysisLabel: `${row.ref} ${row.name}`,
								runLabel: row.completedAt
									? `last run ${dateTime(row.completedAt)}`
									: 'this run',
								rowSummary: data.columns
									.map(
										(column, index) =>
											`${column.label}: ${lineage.values[index] ?? '—'}`,
									)
									.join(' · '),
							}
						: null
				}
			/>
		</Stack>
	);
}
